import type { AuthMode } from '@/lib/constants';

import { AUTH_FLOW, AUTH_QUERY_PARAMS, REFERRAL_QUERY_PARAMS } from '@/lib/constants';
import { baseUrl } from '@/lib/env';

type GithubAuthFlowType = (typeof AUTH_FLOW)[keyof typeof AUTH_FLOW];

type GithubAuthRedirectOptions = {
  flowType: GithubAuthFlowType;
  mode: AuthMode;
  inviteCode?: string | null;
  returnTo?: string;
};

/**
 * Collects the auth-start route params used by both web and desktop GitHub login.
 * The route resolves the final GitHub OAuth URL server-side and needs enough
 * context to send failures back to the correct entrypoint.
 */
function buildGithubAuthRedirectSearchParams({
  flowType,
  mode,
  inviteCode,
  returnTo,
}: GithubAuthRedirectOptions) {
  const searchParams = new URLSearchParams();

  if (flowType === AUTH_FLOW.DESKTOP) {
    searchParams.set(AUTH_QUERY_PARAMS.SOURCE, AUTH_FLOW.DESKTOP);
  }

  if (inviteCode) {
    searchParams.set(REFERRAL_QUERY_PARAMS.INVITE_CODE, inviteCode);
  }

  // Preserve the initiating auth surface so route-level failures can bounce back there.
  searchParams.set(AUTH_QUERY_PARAMS.MODE, mode);

  if (returnTo) {
    searchParams.set(AUTH_QUERY_PARAMS.RETURN_TO, returnTo);
  }

  return searchParams;
}

/** Absolute auth-start URL, for the desktop app to open in the system browser. */
export function buildGithubAuthRedirectUrl(options: GithubAuthRedirectOptions) {
  const authUrl = new URL('/api/auth/github-redirect', baseUrl());
  authUrl.search = buildGithubAuthRedirectSearchParams(options).toString();

  return authUrl.toString();
}

/** Relative auth-start path for web navigation. */
export function buildGithubAuthRedirectPath({
  retryTo,
  ...options
}: GithubAuthRedirectOptions & { retryTo?: string }) {
  const searchParams = buildGithubAuthRedirectSearchParams(options);

  if (retryTo) {
    searchParams.set(AUTH_QUERY_PARAMS.RETRY_TO, retryTo);
  }

  const search = searchParams.toString();
  return search ? `/api/auth/github-redirect?${search}` : '/api/auth/github-redirect';
}
